import React, { useLayoutEffect, useRef, useState } from 'react';
import type { GroupBase, MultiValueProps } from 'react-select';
import Avatar from '../components/Avatar';
import Pill from '../components/Pill';
import addTestId from '../utils/addTestId';
import type { InvitationUserSelectOptionType } from './InvitationUserSelect';
import * as styles from './InvitationUserSelectMultiValue.module.scss';

type InvitationUserSelectMultiValueProps<
  OptionType extends InvitationUserSelectOptionType,
  IsMulti extends boolean = boolean,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
> = MultiValueProps<OptionType, IsMulti, GroupType> & {
  selectProps: MultiValueProps<
    OptionType,
    IsMulti,
    GroupType
  >['selectProps'] & {
    updateValueProps?: {
      valueBeingUpdated: string | null;
      updatingInputValue: string;
      onChangeValueBeingUpdated: (updatedValue: string | null) => void;
      setUpdatingInputValue: (value: string) => void;
      handleBlur: () => void;
    };
  };
};

const InvitationUserSelectMultiValue = <
  OptionType extends InvitationUserSelectOptionType,
  IsMulti extends boolean = boolean,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  children,
  data,
  innerProps,
  isFocused,
  selectProps,
}: InvitationUserSelectMultiValueProps<OptionType, IsMulti, GroupType>) => {
  const {
    valueBeingUpdated,
    updatingInputValue,
    onChangeValueBeingUpdated,
    setUpdatingInputValue,
    handleBlur,
  } = selectProps.updateValueProps ?? {};

  const hiddenValueRef = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState(0);

  const isBeingUpdated = valueBeingUpdated === data.value;

  useLayoutEffect(() => {
    if (hiddenValueRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        const newWidth = hiddenValueRef.current?.getBoundingClientRect()?.width;
        if (typeof newWidth === 'number') {
          setWidth(newWidth);
        }
      });
      resizeObserver.observe(hiddenValueRef.current);
      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, [isBeingUpdated]);

  const onBlur = () => {
    onChangeValueBeingUpdated?.(null);
    handleBlur?.();
  };

  return (
    <div className={styles.container} {...innerProps}>
      {isBeingUpdated ? (
        <>
          <input
            style={{ width: width + 8 }}
            className={styles.updatingInput}
            autoFocus
            onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
              event.target.select();
            }}
            value={updatingInputValue}
            onChange={(e) => setUpdatingInputValue?.(e.target.value)}
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' &&
                updatingInputValue === valueBeingUpdated
              ) {
                onChangeValueBeingUpdated?.(null);
              }
            }}
            onBlur={onBlur}
            {...addTestId('updating-value')}
          />
          <span ref={hiddenValueRef} className={styles.hiddenValue}>
            {updatingInputValue}
          </span>
        </>
      ) : (
        <Pill
          type="user"
          size="small"
          noWrap
          selected={isFocused}
          icon={
            <Avatar size={14} user={{ id: data.value, name: data.label }} />
          }
          onClick={() => {
            onChangeValueBeingUpdated?.(data.value);
          }}
        >
          {children}
        </Pill>
      )}
    </div>
  );
};

export default InvitationUserSelectMultiValue;
